import { useEffect } from 'react'
import type { FC, PropsWithChildren } from 'react'
import { Navigate } from 'react-router'
import { useAuth0 } from '@auth0/auth0-react'
import { useMutation, usePromise } from '~/shared/hooks/promiseState'
import { useSelector } from 'react-redux'
import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'

import { Loader } from '~/shared/ui'
import { getProviders } from '~/app/store/actions/providers'
import { getMe } from '~/app/store/actions/me'
import { setStopSync } from '~/app/store/actions/sync'
import { setSessionToken, setIdToken, requireAccessToken } from '~/shared/lib/auth'
import { usePubNubUtils } from '~/features/member-profile/communication'
import { getAssigneeGroups } from '~/entities/assignee-group'
import SessionNotifier from '~/app/SessionNotifier'
import { useLogoutEffects } from '~/app/auth'
import { useAppDispatch } from '~/app/store/hooks'
import { logger } from '~/shared/lib/logger'

// Gates a route's element behind authentication: renders children once the
// session is established, and redirects to the login screen (preserving the
// requested URL in `?next=`) when it is not.
export const ProtectedRoute: FC<PropsWithChildren> = ({ children }) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently, getIdTokenClaims } = useAuth0()
  const dispatch = useAppDispatch()
  const me = useSelector(state => state.me)
  const { authenticate: authenticateChat } = usePubNubUtils()
  const { handler: handleLogout } = useLogoutEffects({
    auth0: { logoutParams: { returnTo: window.location.origin } },
  })

  // Fetch authentication token and store it before getting "me"
  const { result: getMeSuccessful } = usePromise(async () => {
    try {
      const token = requireAccessToken(await getAccessTokenSilently())
      const idTokenClaims = await getIdTokenClaims()
      const idToken = idTokenClaims?.__raw
      setSessionToken(token)
      if (idToken) {
        setIdToken(idToken)
      }
      await dispatch(getMe())
      authenticateChat()
      return true
    } catch (e) {
      logger.error('[Session] Failed to fetch authentication token', e)
      void handleLogout()
      return false
    }
  })

  const { handler: fetchDependencies } = useMutation(async () => {
    // getProviders dispatches its own error action and resolves; getAssigneeGroups toasts and
    // rethrows, so it is awaited to land in this mutation's error state rather than nowhere.
    void dispatch(getProviders())
    dispatch(setStopSync())
    await dispatch(getAssigneeGroups())
  })

  const redirectUri =
    location.pathname === '/' ? '/' : `/?next=${location.pathname}${location.search}`

  // Gated on `isAuthenticated`: a local logout resets the redux store, which moves `me.id` and
  // re-runs this effect while `getMeSuccessful` still holds its pre-logout `true`. Without the
  // gate that refetch goes out with the session token already cleared and 401s.
  useEffect(() => {
    if (getMeSuccessful === true && isAuthenticated) {
      if (me.id != null) {
        datadogLogs.setUser({ id: me.id.toString() })
        datadogRum.setUser({
          id: me.id.toString(),
          email: me.email,
        })
      }
      void fetchDependencies()
    }
  }, [getMeSuccessful, isAuthenticated, me.id, me.email, fetchDependencies])

  if (isLoading || typeof getMeSuccessful === 'undefined') return <Loader />

  return (
    <>
      <SessionNotifier />
      {isAuthenticated && getMeSuccessful ? (
        children
      ) : (
        <Navigate to={encodeURI(redirectUri)} replace />
      )}
    </>
  )
}
